<template>
	<div class="vbb">

		<!-- ── Config (repliable) ─────────────────────────────────── -->
		<div class="vbb-config">
			<div class="vbb-config-toggle" @click="show_config = !show_config">
				<i class="fas fa-cog"></i> Configuration
				<i :class="show_config ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" style="float:right"></i>
			</div>
			<div v-show="show_config" class="vbb-config-body">

				<div class="vbb-config-row">
					<div class="vbb-field" style="flex:1">
						<label>Titre</label>
						<b-input v-model="title" placeholder="Ex : Validation produits" />
					</div>
					<div class="vbb-field">
						<label>Actif</label>
						<b-form-checkbox v-model="active" switch>{{ active ? 'Oui' : 'Non' }}</b-form-checkbox>
					</div>
				</div>

				<div class="vbb-config-row">
					<div class="vbb-field">
						<label>Colonne id</label>
						<b-input v-model="column_id" style="max-width:160px" />
					</div>
					<div class="vbb-field">
						<label>Sélection colonne id</label>
						<b-form-checkbox v-model="edit_column_id" switch>{{ edit_column_id ? 'Oui' : 'Non' }}</b-form-checkbox>
					</div>
					<div class="vbb-field">
						<label>Intégration</label>
						<b-form-checkbox v-model="integration" switch>{{ integration ? 'Oui' : 'Non' }}</b-form-checkbox>
					</div>
					<div class="vbb-field">
						<label>Données import</label>
						<b-form-checkbox v-model="view_import_data" switch>{{ view_import_data ? 'Afficher' : 'Replier' }}</b-form-checkbox>
					</div>
					<div class="vbb-field">
						<label>Données export</label>
						<b-form-select v-model="view_export_data" :options="[
							{ value: '', text: 'Replier' },
							{ value: 'visible', text: 'Afficher' },
							{ value: 'desactivate', text: 'Désactiver' }
						]" />
					</div>
				</div>

				<div class="vbb-field">
					<label>Données de départ</label>
					<b-form-select v-model="type_data_start" :options="type_data_list" style="max-width:320px" />
				</div>
				<div v-if="type_data_start === 'treatments'" class="vbb-config-sub">
					<div class="vbb-field">
						<label>Projet par défaut</label>
						<b-form-select v-model="type_data_start_project" :options="projects" style="max-width:320px" />
					</div>
				</div>
				<div v-if="type_data_start === 'database'" class="vbb-config-sub">
					<div class="vbb-config-row">
						<div class="vbb-field" style="flex:1">
							<label>Table de départ</label>
							<b-input v-model="type_data_start_table" />
						</div>
						<div class="vbb-field" style="flex:2">
							<label>Conditions de départ</label>
							<b-input v-model="type_data_start_where" />
						</div>
					</div>
				</div>

				<div class="vbb-section-title">Actions</div>
				<actionBuilder :delete_option="true" :projects_obj="projects_obj" :projects="projects" ref="actionBuilder"></actionBuilder>

				<div class="vbb-section-title" style="margin-top:16px">
					<span @click="show_code = !show_code" style="cursor:pointer">
						Code de chargement des données
						<i :class="show_code ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-2"></i>
					</span>
				</div>
				<div v-show="show_code">
					<p class="text-muted small mb-1">Data : tableau des données</p>
					<ace_editor :code="code" @update="update_code($event)"></ace_editor>
				</div>
			</div>
		</div>

		<!-- ── Données de test (repliable) ────────────────────────── -->
		<div class="vbb-config">
			<div class="vbb-config-toggle" @click="show_test_data = !show_test_data">
				<i class="fas fa-database"></i> Données de test
				<i :class="show_test_data ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" style="float:right"></i>
			</div>
			<div v-show="show_test_data" class="vbb-config-body">
				<div class="vbb-field">
					<label>Source des données de test</label>
					<b-form-select v-model="type_data_test" :options="type_data_list" style="max-width:320px" />
				</div>
				<div class="mt-3">
					<edit_tabulator v-if="type_data_test == 'tabulator'" @update="import_file()" ref="tabulator" />
					<DatabaseRequestView v-if="type_data_test == 'DatabaseRequestView'" @update="import_file()" _type="db" :minimal="true" ref="tabulator" />
					<treatment_selector v-if="type_data_test == 'treatments'" :p_project="type_data_start_project" @update="import_file" @add="import_file" ref="tabulator" />
					<splashsync_products v-if="type_data_test == 'splashsync_products'" @update="import_file()" :client="client" ref="tabulator" />
					<splashsync_categories v-if="type_data_test == 'splashsync_categories'" @update="import_file()" :client="client" ref="tabulator" />
					<div v-if="type_data_test == 'database'">
						<database @update="import_file" :change_table="true" :name="false" :edit="false" ref="tabulator" />
					</div>
				</div>
			</div>
		</div>

		<!-- ── Corps principal : canvas + éditeur ────────────────── -->
		<div class="vbb-main">

			<!-- Canvas visuel -->
			<div class="vbb-canvas">

				<!-- État vide -->
				<div v-if="blocks.length === 0" class="vbb-empty">
					<i class="fas fa-th-large vbb-empty-icon"></i>
					<p>Aucun bloc pour le moment.</p>
					<b-button variant="primary" @click="add_line_with_block">
						<i class="fas fa-plus"></i> Créer la première ligne
					</b-button>
				</div>

				<!-- Lignes draggables -->
				<draggable v-else :list="blocks" handle=".vbb-line-handle" class="vbb-lines">
					<div v-for="(line_blocks, id_line) in blocks" :key="line_blocks.uid" class="vbb-line">

						<!-- En-tête de ligne -->
						<div class="vbb-line-header">
							<span class="vbb-line-handle" title="Déplacer la ligne">
								<i class="fas fa-grip-horizontal"></i>
							</span>
							<span class="vbb-line-label">Ligne {{ id_line + 1 }}</span>
							<div class="vbb-line-actions">
								<b-badge v-if="lineSize(id_line) > 12" variant="danger" class="mr-1" :title="'Total : ' + lineSize(id_line) + '/12'">
									<i class="fas fa-exclamation-triangle"></i> {{ lineSize(id_line) }}/12
								</b-badge>
								<b-button variant="link" size="sm" title="Ajouter un bloc" @click="addAndSelect(id_line)">
									<i class="fas fa-plus"></i>
								</b-button>
								<b-button variant="link" size="sm" title="Supprimer la ligne" class="vbb-btn-danger" @click="askDelLine(id_line)">
									<i class="fas fa-trash"></i>
								</b-button>
							</div>
						</div>

						<!-- Blocs draggables (inter-lignes via group="blocks") -->
						<draggable
							:list="line_blocks.blocks"
							group="blocks"
							@end="onBlockDragEnd"
							class="vbb-blocks-row"
							:class="{ 'vbb-blocks-row--empty': line_blocks.blocks.length === 0 }"
						>
							<div
								v-for="(block, idx) in line_blocks.blocks"
								:key="block.uid"
								class="vbb-block"
								:class="['vbb-block--' + (block.type || 'none'), view_id === block.uid ? 'vbb-block--selected' : '']"
								:style="{ flex: block.size > 0 ? block.size : 1 }"
								@click="set_view_id(block.uid)"
							>
								<span :class="'vbb-type-badge vbb-type-badge--' + (block.type || 'none')">{{ typeName(block.type) }}</span>
								<div class="vbb-block-name">{{ block.name || '(sans nom)' }}</div>
								<div class="vbb-block-size-hint">{{ block.size > 0 ? block.size + '/12' : 'auto' }}</div>
							</div>
						</draggable>
					</div>
				</draggable>

				<!-- Zone de dépôt → nouvelle ligne -->
				<draggable
					:list="new_line_blocks"
					group="blocks"
					@add="onDropToNewLine"
					class="vbb-new-line-zone"
				>
					<div v-if="new_line_blocks.length === 0" class="vbb-new-line-hint">
						<i class="fas fa-arrow-down"></i> Déposer ici pour créer une nouvelle ligne
					</div>
				</draggable>

				<!-- Bouton ajout bloc -->
				<div class="vbb-canvas-toolbar">
					<b-button variant="outline-secondary" size="sm" @click="add_line_with_block">
						<i class="fas fa-plus"></i> Nouveau bloc
					</b-button>
				</div>

			</div>

			<!-- Panneau éditeur (v-show contrôle la visibilité) -->
			<div class="vbb-editor" :class="{ 'vbb-editor--open': !!view_id }">

				<!-- Placeholder si rien de sélectionné -->
				<div v-show="!view_id" class="vbb-editor-placeholder">
					<i class="fas fa-mouse-pointer"></i>
					<p>Cliquez sur un bloc pour l'éditer.</p>
				</div>

				<!-- Éditeur de bloc -->
				<div v-for="(line_blocks, id_line) in blocks" :key="'block-editors-' + line_blocks.uid">
					<div
						v-for="(block, idx) in line_blocks.blocks"
						:key="'block-editor-' + block.uid"
						v-show="block.uid === view_id"
					>
						<div class="vbb-editor-header">
							<span :class="'vbb-type-badge vbb-type-badge--' + (block.type || 'none')">{{ typeName(block.type) }}</span>
							{{ block.name || 'nouveau bloc' }}
						</div>
						<div class="vbb-editor-body">
							<div class="vbb-field">
								<label>Nom</label>
								<b-input v-model="block.name" />
							</div>
							<div class="vbb-field">
								<label>Type</label>
								<b-select v-model="block.type" :options="types" />
							</div>
							<div class="vbb-field">
								<label>Longueur
									<small class="text-muted ml-2" v-if="block.size > 0">
										{{ block.size }}/12 (~{{ Math.round(block.size / 12 * 100) }}%)
									</small>
									<small class="text-muted ml-2" v-else>non défini</small>
								</label>
								<div class="vbb-size-selector" @mouseleave="hoverSize = 0">
									<div
										v-for="n in 12" :key="n"
										:class="[
											'vbb-size-cell',
											n <= (block.size || 0) ? 'vbb-size-cell--on' : '',
											hoverSize > 0 && n <= hoverSize && n > (block.size || 0) ? 'vbb-size-cell--hover' : '',
											hoverSize > 0 && n <= (block.size || 0) && n > hoverSize ? 'vbb-size-cell--hover-off' : ''
										]"
										@click="block.size = n"
										@mouseover="hoverSize = n"
										:title="n + '/12'"
									></div>
								</div>
								<b-button variant="link" size="sm" class="p-0 mt-1"
										  :class="{ 'font-weight-bold': !block.size }"
										  @click="block.size = ''">
									↺ Auto
								</b-button>
							</div>
							<div class="vbb-field">
								<b-form-checkbox v-model="block.hide_big_block">Cacher la vue agrandie</b-form-checkbox>
							</div>

							<h6 class="vbb-params-title">Paramètres</h6>
							<textBuilder :params="block.params" :ref="block.uid" v-if="block.type === 'text'"></textBuilder>
							<arrayBuilder :projects="projects" :projects_obj="projects_obj" :params="block.params" :ref="block.uid" v-if="block.type === 'array'"></arrayBuilder>

							<div class="vbb-editor-del">
								<b-button variant="outline-danger" size="sm" @click="askDel(id_line, idx)">
									<i class="fas fa-trash"></i> Supprimer ce bloc
								</b-button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ── Barre d'actions ─────────────────────────────────────── -->
		<div class="vbb-topbar">
			<b-button variant="primary" size="sm" @click="preview">
				<i class="fas fa-eye"></i> Prévisualiser
			</b-button>
			<test_as_user @change="onTestUserChange"></test_as_user>
		</div>

		<!-- Prévisualisation -->
		<validation ref="validation" :minimal="true" :import_data="test_data" :test_jwt="test_user ? test_user.jwt : null"></validation>

		<!-- Modal de confirmation -->
		<b-modal
			ref="confirm_modal"
			title="Confirmer la suppression"
			ok-variant="danger"
			ok-title="Supprimer"
			cancel-title="Annuler"
			@ok="doConfirm"
		>
			{{ confirm_message }}
		</b-modal>
	</div>
</template>

<script>
	import uniqueId from "lodash.uniqueid";
	import draggable from "vuedraggable";

	import splashsync_categories from "../components/splashsync_categories.vue";
	import splashsync_products from "../components/splashsync_products.vue";
	import actionBuilder from "../components/validation/actionBuilder.vue";
	import treatment_selector from "../components/treatment_selector.vue";
	import arrayBuilder from "../components/validation/arrayBuilder.vue";
	import textBuilder from "../components/validation/textBuilder.vue";
    import DatabaseRequestView from '../views/DatabaseRequestView.vue';
	import edit_tabulator from "../components/edit_tabulator.vue";
	import ace_editor from "../components/ace_editor.vue";
	import databaseView from "../views/database.vue";
	import projects from "../services/projects.js";
	import validation from "./Validation.vue";    
	import users from "../services/users.js";
	import tools from "../utils/Tools";
	import test_as_user from "../components/TestAsUser.vue";

	var clone = require("clone");

	export default {
		name: "validation_builder",
		components: {
			validation,
			edit_tabulator,
			database: databaseView,
			DatabaseRequestView,
			arrayBuilder,
			textBuilder,
			draggable,
			actionBuilder,
			treatment_selector,
			ace_editor,
			splashsync_products,
			splashsync_categories,
			test_as_user
		},
		props: {},
		data() {
			return {
				id: uniqueId(""),

				title: "",
				blocks: [],
				column_id: "",
				edit_column_id: false,
				code: "",
				client: null,

				types: [
					{ value: "text",  text: "Texte" },
					{ value: "array", text: "Tableau" }
				],

				typeLabels: { text: "Txt", array: "Arr" },

				type_data_list: [
					{ value: 'tabulator',          text: 'Tableau' },
					{ value: 'database',           text: 'Base de données' },
					{ value: 'DatabaseRequestView',text: 'Base de données - multitables' },
					{ value: 'treatments',         text: 'Traitements' },
				],

				view_id: null,
				show_config: false,
				show_test_data: true,
				show_code: false,

				// Zone de dépôt pour créer une nouvelle ligne par drag-and-drop
				new_line_blocks: [],

				// Confirmation suppression
				confirm_message: "",
				confirm_callback: null,

				test_data: [],
				type_data_test: "tabulator",
				type_data_start: "tabulator",
				type_data_start_table: "",
				type_data_start_where: "",
				type_data_start_project: "",

				projects: [],
				projects_obj: {},

				integration: false,
				active: true,

				view_import_data: false,
				view_export_data: "",
				hoverSize: 0,

				test_user: null
			};
		},
		methods: {

			onTestUserChange(test_user) {
				this.test_user = test_user;
			},

			// ── Helpers ────────────────────────────────────────────

			typeName(type) {
				return this.typeLabels[type] || (type ? type : "?");
			},

			lineSize(id_line) {
				if (!this.blocks[id_line]) return 0;
				return this.blocks[id_line].blocks.reduce((sum, b) => {
					const s = parseInt(b.size);
					return sum + (s > 0 ? s : 0);
				}, 0);
			},

			// ── Sélection ─────────────────────────────────────────

			set_view_id(id) {
				this.view_id = id;
			},

			// ── Données de test ───────────────────────────────────

			update_code(code) {
				this.code = code;
			},

			import_file(add, data = null) {
				if (!data)
					data = this.$refs.tabulator.get_data();
				if (add) {
					data = tools.standardize_json_all(this.test_data.concat(data));
				}
				this.test_data = data;
			},

			// ── Aperçu ────────────────────────────────────────────

			async preview() {
				const data = await this.save();
				await this.$refs.validation.load(clone(data));
			},

			// ── Chargement ────────────────────────────────────────

			async load(data) {
				const self = this;

				this.title         = data.title;
				this.blocks        = data.blocks || [];
				this.column_id     = data.column_id || "";
				this.edit_column_id = data.edit_column_id || false;

				if (data.hasOwnProperty("active"))         this.active = data.active;
				if (data.type_data_start)                  this.type_data_start = data.type_data_start;
				if (data.type_data_start_table)            this.type_data_start_table = data.type_data_start_table;
				if (data.type_data_start_where)            this.type_data_start_where = data.type_data_start_where;
				if (data.type_data_test)                   this.type_data_test = data.type_data_test;
				if (data.integration)                      this.integration = data.integration;
				if (data.type_data_start_project)          this.type_data_start_project = data.type_data_start_project;
				if (data.code)                             this.code = data.code;
				this.view_import_data = data.view_import_data || false;
				this.view_export_data = data.view_export_data || "";

				if (data.actions)
					this.$refs.actionBuilder.load(data.actions);

				self.$nextTick(async function () {
					for (let i = 0; i < self.blocks.length; i++) {
						for (let j = 0; j < self.blocks[i].blocks.length; j++) {
							const ref = self.$refs[self.blocks[i].blocks[j].uid];
							if (ref && ref[0])
								await ref[0].load(self.blocks[i].blocks[j].params);
						}
					}
					if (data.test_data)
						self.$refs.tabulator.load(data.test_data);
				});

				await this.$refs.validation.load(data);
			},

			// ── Sauvegarde ────────────────────────────────────────

			async save() {
				for (let i = 0; i < this.blocks.length; i++) {
					for (let j = 0; j < this.blocks[i].blocks.length; j++) {
						const ref = this.$refs[this.blocks[i].blocks[j].uid];
						if (ref && ref[0]) {
							this.blocks[i].blocks[j].params = await ref[0].save();
							this.blocks[i].blocks[j].params.block_i = i;
							this.blocks[i].blocks[j].params.block_j = j;
						}
					}
				}

				const test_data = await this.$refs.tabulator.save();
				const actions   = await this.$refs.actionBuilder.save();

				return {
					title:                   this.title,
					blocks:                  this.blocks,
					test_data,
					type_data_test:          this.type_data_test,
					column_id:               this.column_id,
					edit_column_id:          this.edit_column_id,
					actions,
					type_data_start:         this.type_data_start,
					type_data_start_table:   this.type_data_start_table,
					type_data_start_where:   this.type_data_start_where,
					type_data_start_project: this.type_data_start_project,
					integration:             this.integration,
					active:                  this.active,
					data_db:                 { active: this.active },
					code:                    this.code,
					view_import_data:        this.view_import_data,
					view_export_data:        this.view_export_data
				};
			},

			// ── Ajout ─────────────────────────────────────────────

			async add_line_with_block() {
				const lineUid  = this.$uuid.v4();
				const blockUid = this.$uuid.v4();
				this.blocks.push({ uid: lineUid, blocks: [{ uid: blockUid, name: "", type: "", size: "", params: {}, hide_big_block: false }] });
				await this.$nextTick();
				this.view_id = blockUid;
			},

			async addAndSelect(id_line) {
				const uid = this.$uuid.v4();
				this.blocks[id_line].blocks.push({ uid, name: "", type: "", size: "", params: {}, hide_big_block: false });
				await this.$nextTick();
				this.view_id = uid;
			},

			// Rétro-compatibilité
			async add_line() { await this.add_line_with_block(); },
			async add(id_line) { await this.addAndSelect(id_line); },

			// ── Suppression avec confirmation ─────────────────────

			askDel(id_line, idx) {
				const name = this.blocks[id_line].blocks[idx].name || "ce bloc";
				this.confirm_message  = `Supprimer le bloc « ${name} » ? Cette action est irréversible.`;
				this.confirm_callback = () => this.del(id_line, idx);
				this.$refs.confirm_modal.show();
			},
			askDelLine(id_line) {
				const count = this.blocks[id_line].blocks.length;
				this.confirm_message  = `Supprimer la ligne ${id_line + 1} et ses ${count} bloc(s) ? Cette action est irréversible.`;
				this.confirm_callback = () => this.del_line(id_line);
				this.$refs.confirm_modal.show();
			},
			doConfirm() {
				if (this.confirm_callback) {
					this.confirm_callback();
					this.confirm_callback = null;
				}
			},

			del(id_line, idx) {
				const uid = this.blocks[id_line].blocks[idx].uid;
				this.blocks[id_line].blocks.splice(idx, 1);
				if (this.view_id === uid) this.view_id = null;
				if (this.blocks[id_line].blocks.length === 0)
					this.blocks.splice(id_line, 1);
			},
			del_line(id_line) {
				for (const block of this.blocks[id_line].blocks) {
					if (this.view_id === block.uid) this.view_id = null;
				}
				this.blocks.splice(id_line, 1);
			},

			// ── Drag-and-drop ─────────────────────────────────────

			onBlockDragEnd() {
				for (let i = this.blocks.length - 1; i >= 0; i--) {
					if (this.blocks[i].blocks.length === 0)
						this.blocks.splice(i, 1);
				}
			},

			onDropToNewLine() {
				if (this.new_line_blocks.length === 0) return;
				const dropped = this.new_line_blocks.splice(0, this.new_line_blocks.length);
				const lineUid = this.$uuid.v4();
				this.blocks.push({ uid: lineUid, blocks: dropped });
				this.$nextTick(() => {
					if (dropped.length > 0) this.view_id = dropped[0].uid;
				});
			},
		},
		mounted: async function () {
			const response = await users.getSelf(true);
			this.client = response.client;
			if (response.client && response.client.details && response.client.details.splash) {
				this.type_data_list.push({ value: 'splashsync_products',   text: 'Splash Sync - produits' });
				this.type_data_list.push({ value: 'splashsync_categories', text: 'Splash Sync - catégories' });
			}

			const list_projects = await projects.getAll(false, false, null, true);
			for (const p of list_projects) {
				this.projects_obj[p.uid] = p;
				this.projects.push({ value: p.uid, text: p.libelle });
			}
		},
	};
</script>

<style scoped>
/* ── Conteneur principal ───────────────────────────────────────── */
.vbb {
	font-family: inherit;
}

/* ── Barre d'actions persistante ──────────────────────────────── */
.vbb-topbar {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	margin-bottom: 12px;
}

/* ── Config (accordéon) ────────────────────────────────────────── */
.vbb-config {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	margin-bottom: 16px;
	overflow: hidden;
}
.vbb-config-toggle {
	padding: 10px 14px;
	background: #f7f7f7;
	cursor: pointer;
	font-weight: 600;
	user-select: none;
}
.vbb-config-toggle:hover { background: #efefef; }
.vbb-config-body {
	padding: 14px;
	border-top: 1px solid #e0e0e0;
}
.vbb-config-sub {
	margin-top: 10px;
	padding-left: 12px;
	border-left: 3px solid #dee2e6;
}
.vbb-config-row {
	display: flex;
	gap: 24px;
	align-items: flex-start;
	flex-wrap: wrap;
	margin-bottom: 8px;
}
.vbb-field {
	margin-bottom: 12px;
}
.vbb-field label {
	display: block;
	font-size: 0.82rem;
	font-weight: 600;
	color: #555;
	margin-bottom: 4px;
}
.vbb-section-title {
	font-size: 0.82rem;
	font-weight: 700;
	text-transform: uppercase;
	color: #888;
	letter-spacing: .05em;
	margin-bottom: 8px;
}

/* ── Layout main ───────────────────────────────────────────────── */
.vbb-main {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

/* ── Canvas ────────────────────────────────────────────────────── */
.vbb-canvas {
	width: 100%;
	min-width: 0;
}
.vbb-lines {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* État vide */
.vbb-empty {
	text-align: center;
	padding: 48px 24px;
	border: 2px dashed #ccc;
	border-radius: 10px;
	color: #888;
}
.vbb-empty-icon {
	font-size: 2.5rem;
	margin-bottom: 12px;
	color: #bbb;
}

/* ── Ligne ─────────────────────────────────────────────────────── */
.vbb-line {
	border: 1px solid #dde;
	border-radius: 8px;
	overflow: hidden;
	background: #fafafa;
}
.vbb-line-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 5px 10px;
	background: #eef0f5;
	border-bottom: 1px solid #dde;
	font-size: 0.82rem;
}
.vbb-line-handle {
	cursor: grab;
	color: #aaa;
	font-size: 1rem;
}
.vbb-line-handle:active { cursor: grabbing; }
.vbb-line-label {
	font-weight: 600;
	color: #555;
}
.vbb-line-actions {
	margin-left: auto;
	display: flex;
	gap: 0;
}
.vbb-line-actions .btn-link { color: #666; padding: 2px 6px; }
.vbb-line-actions .btn-link:hover { color: #333; }
.vbb-btn-danger.btn-link { color: #c0392b !important; }

/* ── Rangée de blocs ───────────────────────────────────────────── */
.vbb-blocks-row {
	display: flex;
	min-height: 70px;
	padding: 8px;
	gap: 8px;
}
.vbb-blocks-row--empty {
	border: 2px dashed #d0d0d0;
	background: #f5f5f5;
	justify-content: center;
	align-items: center;
	color: #bbb;
	font-size: 0.8rem;
}

/* ── Bloc ──────────────────────────────────────────────────────── */
.vbb-block {
	min-width: 60px;
	border-radius: 6px;
	padding: 8px 10px;
	background: #fff;
	border: 2px solid #dde;
	cursor: pointer;
	transition: border-color .15s, box-shadow .15s;
	display: flex;
	flex-direction: column;
	gap: 4px;
	position: relative;
}
.vbb-block:hover {
	border-color: #97b5e0;
	box-shadow: 0 1px 6px rgba(0,100,200,.1);
}
.vbb-block--selected {
	border-color: #0070cc !important;
	box-shadow: 0 0 0 3px rgba(0,112,204,.18) !important;
}
.vbb-block-name {
	font-size: 0.82rem;
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.vbb-block-size-hint {
	font-size: 0.7rem;
	color: #aaa;
}

/* Types de blocs : couleurs */
.vbb-block--text  { border-left: 4px solid #2980b9; }
.vbb-block--array { border-left: 4px solid #27ae60; }
.vbb-block--none  { border-left: 4px solid #bbb; }

/* ── Badges type ───────────────────────────────────────────────── */
.vbb-type-badge {
	display: inline-block;
	font-size: 0.65rem;
	font-weight: 700;
	padding: 1px 5px;
	border-radius: 3px;
	text-transform: uppercase;
	background: #eee;
	color: #555;
}
.vbb-type-badge--text  { background: #d6eaf8; color: #1a5276; }
.vbb-type-badge--array { background: #d5f5e3; color: #1e8449; }

/* ── Zone nouvelle ligne ───────────────────────────────────────── */
.vbb-new-line-zone {
	margin-top: 10px;
	min-height: 44px;
	border: 2px dashed #c0cfe0;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background .2s;
}
.vbb-new-line-zone:hover { background: #eef4fb; }
.vbb-new-line-hint {
	color: #aab8c8;
	font-size: 0.8rem;
	pointer-events: none;
}

/* ── Barre d'outils canvas ─────────────────────────────────────── */
.vbb-canvas-toolbar {
	margin-top: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
}

/* ── Panneau éditeur ───────────────────────────────────────────── */
.vbb-editor {
	width: 100%;
	border: 1px solid #dde;
	border-radius: 8px;
	background: #fff;
}
.vbb-editor-placeholder {
	padding: 32px 20px;
	text-align: center;
	color: #bbb;
	font-size: 0.88rem;
}
.vbb-editor-placeholder i { font-size: 1.5rem; display: block; margin-bottom: 8px; }
.vbb-editor-header {
	padding: 10px 14px;
	background: #f0f4f8;
	border-bottom: 1px solid #dde;
	font-weight: 600;
	font-size: 0.9rem;
	display: flex;
	align-items: center;
	gap: 8px;
}
.vbb-editor-body {
	padding: 14px;
}
.vbb-params-title {
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid #eee;
	font-size: 0.82rem;
	text-transform: uppercase;
	color: #888;
	letter-spacing: .05em;
}
.vbb-editor-del {
	margin-top: 20px;
	padding-top: 12px;
	border-top: 1px solid #eee;
}

/* ── Sélecteur de taille ───────────────────────────────────────── */
.vbb-size-selector {
	display: flex;
	gap: 3px;
	margin-bottom: 4px;
}
.vbb-size-cell {
	flex: 1;
	height: 22px;
	border-radius: 3px;
	background: #e9ecef;
	cursor: pointer;
	transition: background .12s;
	border: 1px solid #dee2e6;
}
.vbb-size-cell--on { background: #0070cc; border-color: #005fa3; }
.vbb-size-cell--hover { background: #55a0e0; border-color: #3d8fce; }
.vbb-size-cell--hover-off { background: #c8dff5; border-color: #a8c8e8; }
</style>
